import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { Color } from '../../../styles/Color';
import { Shadow } from '../../../styles/Shadow';
import PoiNoImage from '../../../assets/icons/hide_image.svg';
import { RouterLink } from '../../../components/common/link/RouterLink';
import { ICON_GAP_TRANSITION } from '../../../styles/animation/iconGapTransition';
import { Css, Flex, H5, Responsive } from '../../../ui';
import { QuickLink } from '../../../components/common/quick-links/QuickLink';

interface IPoiCardProps {
	title: string;
	linkHref: string;
	icon?: ReactNode;
	location?: string;
	backgroundImage?: string;
}

export default function PoiCard({
	title,
	linkHref,
	icon,
	location,
	backgroundImage,
}: IPoiCardProps) {
	return (
		<li>
			<StyledPoiLink href={linkHref}>
				<PoiCardContainer>
					{backgroundImage ? (
						<PoiImageWrap>
							<PoiImage src={backgroundImage} alt={title} loading="lazy" />
						</PoiImageWrap>
					) : (
						<PoiNoImageWrap>
							<PoiNoImage />
						</PoiNoImageWrap>
					)}

					<PoiCardContent>
						<PoiCardHeader>
							<PoiCardTitle>{title}</PoiCardTitle>
							{icon && <PoiCardIcon>{icon}</PoiCardIcon>}
						</PoiCardHeader>

						<PoiCardMeta direction="column" justifyContent="space-between">
							<Flex direction="row" justifyContent="space-between">
								<div>
									{location && (
										<PoiCardBadge className="small">{location}</PoiCardBadge>
									)}
								</div>
								<QuickLink
									url={linkHref}
									aria-label="POI Details"
									type="internal"
								/>
							</Flex>
						</PoiCardMeta>
					</PoiCardContent>
				</PoiCardContainer>
			</StyledPoiLink>
		</li>
	);
}

const StyledPoiLink = styled(RouterLink)`
	text-decoration: none;
`;

const PoiCardContent = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: ${Css.pxToRem(16)};
	gap: ${Css.pxToRem(8)};
`;

export const PoiImageWrap = styled.div`
	width: ${Css.pxToRem(144)};
	padding: ${Css.pxToRem(12)};
	aspect-ratio: 1/1;
	height: 100%;

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		width: ${Css.pxToRem(120)};
		padding: ${Css.pxToRem(10)};
	}
`;

const PoiCardHeader = styled.div`
	display: flex;
	justify-content: space-between;
	gap: ${Css.pxToRem(8)};
`;

export const PoiCardContainer = styled.div`
	height: ${Css.pxToRem(144)};
	display: flex;

	position: relative;
	overflow: hidden;
	box-shadow: ${Shadow.Desktop};
	background-color: ${Color.PrimaryWhite};
	transition: transform 0.3s ease-out;
	border-radius: ${Css.pxToRem(8)};

	&:hover {
		transform: translate(0, ${Css.pxToRem(-10)});
		${PoiCardHeader} svg {
			path {
				fill: ${Color.PrimaryBlue1};

		}
	}

	svg {
		transition: ${`transform ${ICON_GAP_TRANSITION}`};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		box-shadow: ${Shadow.Mobile};

		&:hover {
			transform: none;
		}
	}
`;

const PoiNoImageWrap = styled.div`
	object-fit: cover;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const PoiImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: ${Css.pxToRem(4)};
`;

const PoiCardMeta = styled(Flex)``;

const PoiCardTitle = styled(H5)`
	margin: 0;
	font-size: ${Css.pxToRem(24)};
	line-height: ${Css.pxToRem(28)};
	padding-bottom: ${Css.pxToRem(4)};

	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		font-size: ${Css.pxToRem(20)};
		line-height: ${Css.pxToRem(24)};
	}
`;

const PoiCardBadge = styled.span`
	min-height: ${Css.pxToRem(24)};
	display: inline-block;
	background-color: ${Color.SecondaryHovered};
	padding: ${Css.pxToRem(3)} ${Css.pxToRem(8)};
	border-radius: ${Css.pxToRem(32)};
	font-weight: 700;
`;

const PoiCardIcon = styled.span`
	svg {
		path {
			fill: currentColor;
		}
	}
`;
